import * as React from "react";

// Require Editor JS files.
import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/third_party/embedly.min.js";

// // Require Editor CSS files.
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/third_party/embedly.min.css";
import "froala-editor/css/plugins.pkgd.min.css";
import "froala-editor/css/third_party/font_awesome.min.css";
import "froala-editor/css/third_party/image_tui.min.css";
import "froala-editor/css/third_party/spell_checker.min.css";

// Require Font Awesome.
import Froala from "react-froala-wysiwyg";
import "./styles.css";
import { useAtendimentoPage } from "../../../../../Context/AtendimentoPage";
import { InserirArquivoDriveController } from "../../../../../Controllers/Geral/Integracao/IntegracoesController";
import { getBase64URL } from "../../../../../Tools/ProcessingTools";
import { useTransformToRef } from "../../../../../hooks/useTransformToRef";
import { Dispatch, SetStateAction } from "react";

interface IFroalaEditorProps {
    value: string;
    onChange: (html: string) => void;
    onImageUploadStart?: () => void;
    onImageUploadEnd?: () => void;
    setUploadedImages?: Dispatch<SetStateAction<string[]>>;
}

export function FroalaEditor(props: IFroalaEditorProps) {
    const froalaLicenseKey = process.env.REACT_APP_FROALA_KEY;

    const { ID_CASO } = useAtendimentoPage();

    const onImageUploadStartRef = useTransformToRef(props.onImageUploadStart);
    const onImageUploadEndRef = useTransformToRef(props.onImageUploadEnd);

    let imagensUpload: string[] = [];

    let arrayImagens: string[] = [];

    const handleImageBeforeUpload = async (files: File[]) => {
        const file = files[0];

        if (file) {
            const image = {
                url: "",
            };
            if (ID_CASO) {
                onImageUploadStartRef.current?.();
                const imageUrl = await InserirArquivoDriveController(
                    ID_CASO,
                    file
                );

                imagensUpload.push(imageUrl);
                
                if(props.setUploadedImages) {
                    props?.setUploadedImages(imagensUpload);
                }

                onImageUploadEndRef.current?.();
            } else {
                image.url = await getBase64URL(file);
                imagensUpload.push(image.url);

                if(props.setUploadedImages) {
                    props?.setUploadedImages(imagensUpload);
                }
            }
        }
    };

    const handleImageRemoved = (img: any) => {
        let removida: string = img[0].currentSrc;
        let index: number = arrayImagens.findIndex((imagem) => imagem === removida);
        arrayImagens.splice(index, 1);

        if (props.setUploadedImages) {
            props.setUploadedImages(prevImages => { 
                prevImages.splice(index, 1);
                return prevImages;
            });
        }
    };
    
    const handleImageInsert = (img: any) => {
        arrayImagens.push(img[0].currentSrc)
    };

    return (
        <div className="FroalaEditor">
            <Froala
                model={props.value}
                onModelChange={props.onChange}
                tag="textarea"
                config={{
                    key: froalaLicenseKey,
                    iframe: false,
                    attribution: false,
                    placeholderText: "Start typing...",
                    events: {
                        "image.beforeUpload": handleImageBeforeUpload,
                        "image.inserted": handleImageInsert,
                        "image.removed": (src: any) => handleImageRemoved(src),
                    },                    
                    wordPasteModal: false,
                    wordPasteKeepFormatting: true,
                    fontFamily: {
                        'Arial, sans-serif': 'Arial',
                        'Verdana, sans-serif': 'Verdana',
                        'Tahoma, Geneva, sans-serif': 'Tahoma',
                        'Times New Roman, serif': 'Times New Roman',
                        'Georgia, serif': 'Georgia',
                        'Palatino Linotype, Book Antiqua, Palatino, serif': 'Palatino Linotype',
                        'Helvetica, Arial, sans-serif': 'Helvetica',
                        'Courier New, monospace': 'Courier New',
                        'Lucida Console, Monaco, monospace': 'Lucida Console',
                        'Symbol, sans-serif': 'Symbol',
                        'Webdings, sans-serif': 'Webdings',
                        'Wingdings, Zapf Dingbats, sans-serif': 'Wingdings',
                        'Comic Sans MS, sans-serif': 'Comic Sans MS',
                        'Impact, Charcoal, sans-serif': 'Impact',
                        'Copperplate, Copperplate Gothic Light, sans-serif': 'Copperplate',
                        'Garamond, Baskerville, Baskerville Old Face, serif': 'Garamond',
                        'Bookman Old Style, Book Antiqua, Goudy, serif': 'Bookman Old Style',
                        'Century Gothic, sans-serif': 'Century Gothic',
                        'Courier, monospace': 'Courier',
                        'Franklin Gothic Medium, sans-serif': 'Franklin Gothic Medium',
                        'Helvetica Neue, Arial, sans-serif': 'Helvetica Neue',
                        'Arial Black, sans-serif': 'Arial Black',
                        'Verdana, Geneva, sans-serif': 'Verdana',
                        'Palatino, serif': 'Palatino',
                        'Trebuchet MS, sans-serif': 'Trebuchet MS',
                        'Gill Sans, sans-serif': 'Gill Sans',
                        'Century Schoolbook, serif': 'Century Schoolbook',
                        'Didot, serif': 'Didot',
                        'Brush Script MT, cursive': 'Brush Script MT',
                        'Curlz MT, cursive': 'Curlz MT',
                        'Futura, sans-serif': 'Futura',
                        'Helvetica, sans-serif': 'Helvetica',
                        'Avant Garde, sans-serif': 'Avant Garde',
                        'Bodoni MT, serif': 'Bodoni MT',
                        'Rockwell, serif': 'Rockwell',
                        'Segoe UI, sans-serif': 'Segoe UI',
                        'Algerian, cursive': 'Algerian',
                        'Monotype Corsiva, cursive': 'Monotype Corsiva',
                        'Edwardian Script ITC, cursive': 'Edwardian Script ITC',
                        'Stencil, fantasy': 'Stencil',
                        'Papyrus, fantasy': 'Papyrus',
                        'Zapf Chancery, cursive': 'Zapf Chancery',
                        'Cooper Black, cursive': 'Cooper Black',
                        'OCR A Extended, monospace': 'OCR A Extended',
                        'Segoe Print, sans-serif': 'Segoe Print',
                    },
                    fontFamilySelection: true,
                    toolbarButtons: {
                        moreText: {
                            buttons: [
                                "bold",
                                "italic",
                                "underline",
                                "strikeThrough",
                                "subscript",
                                "superscript",
                                "fontFamily",
                                "fontSize",
                                "textColor",
                                "backgroundColor",
                                "inlineClass",
                                "inlineStyle",
                                "clearFormatting",
                            ],
                        },
                        moreParagraph: {
                            buttons: [
                                "alignLeft",
                                "alignCenter",
                                "formatOLSimple",
                                "alignRight",
                                "alignJustify",
                                "formatOL",
                                "formatUL",
                                "paragraphFormat",
                                "paragraphStyle",
                                "lineHeight",
                                "outdent",
                                "indent",
                                "quote",
                            ],
                        },
                        moreRich: {
                            buttons: [
                                "insertLink",
                                "insertImage",
                                "insertTable",
                                "insertFile",
                                "fontAwesome",
                                "specialCharacters",
                                "insertHR",
                            ],
                        },
                        moreMisc: {
                            buttons: [
                                "undo",
                                "redo",
                                "help",
                                "fullscreen",
                                "html",
                                "selectAll",
                                "print",
                            ],
                            align: "right",
                            buttonsVisible: 4,
                        },
                    },
                    pluginsEnabled: [
                        "table",
                        "spell",
                        "quote",
                        "save",
                        "quickInsert",
                        "paragraphFormat",
                        "paragraphStyle",
                        "help",
                        "draggable",
                        "align",
                        "link",
                        "lists",
                        "file",
                        "image",
                        "emoticons",
                        "print",
                        "codeView",
                        "url",
                        "fullscreen",
                        "video",
                        "embedly",
                        "colors",
                        "entities",
                        "inlineClass",
                        "inlineStyle",
                        "fontFamily",
                        "fontSize",
                        "wordPaste",
                        // 'codeBeautif '
                        // 'spellChecker',
                        "imageTUI",
                    ],
                }}
            />
        </div>
    );
}

// Include special components if required.
// import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
// import FroalaEditorA from 'react-froala-wysiwyg/FroalaEditorA';
// import FroalaEditorButton from 'react-froala-wysiwyg/FroalaEditorButton';
// import FroalaEditorImg from 'react-froala-wysiwyg/FroalaEditorImg';
// import FroalaEditorInput from 'react-froala-wysiwyg/FroalaEditorInput';

// Render Froala Editor component.
